/**
 * SearchInput — Search bar with icon, input, and optional clear button
 *
 * Also works as a tappable link when editable={false} + onPress.
 * Used for: search bars, filter inputs, navigation search triggers.
 */
import type { ReactNode } from 'react'
import { Search, X } from 'lucide-react-native'
import { styled, XStack, Input as TamaguiInput, type GetProps, useTheme } from 'tamagui'
import { Text } from '../elements/Text'

const SearchFrame = styled(XStack, {
  name: 'SearchInput',
  ai: 'center',
  bg: '$backgroundCard',
  br: '$full',
  px: '$md',
  h: 48,
  gap: 10,
  cursor: 'pointer',
  pressStyle: { opacity: 0.7 },
})

const SearchField = styled(TamaguiInput, {
  name: 'SearchField',
  f: 1,
  bg: 'transparent',
  bw: 0,
  py: 10,
  fontSize: 14,
  color: '$color',
  placeholderTextColor: '$colorSecondary',
  focusStyle: { bw: 0 },
})

type SearchFrameProps = GetProps<typeof SearchFrame>

interface SearchInputProps extends Omit<SearchFrameProps, 'onChangeText'> {
  value?: string
  onChangeText?: (text: string) => void
  placeholder?: string
  icon?: ReactNode
  clearIcon?: ReactNode
  editable?: boolean
}

export const SearchInput = SearchFrame.styleable<SearchInputProps>(
  ({ value = '', onChangeText, placeholder = 'Search...', icon, clearIcon, editable = true, ...props }, ref) => {
    const theme = useTheme()
    const iconColor = String(theme.colorSecondary?.val ?? '#888')
    return (
      <SearchFrame ref={ref} {...props}>
        {icon ?? <Search size={18} color={iconColor} />}
        {editable ? (
          <SearchField
            value={value}
            onChangeText={onChangeText}
            placeholder={placeholder}
          />
        ) : (
          <Text preset="bodySmall" tone="secondary" f={1}>{placeholder}</Text>
        )}
        {editable && value.length > 0 && (
          <XStack
            role="button"
            cursor="pointer"
            onPress={() => onChangeText?.('')}
            p="$xs"
          >
            {clearIcon ?? <X size={14} color={iconColor} />}
          </XStack>
        )}
      </SearchFrame>
    )
  },
)
